import React, { useRef, useState, useEffect } from "react";
import { cn } from "@/lib/utils";

const defaultOptions = {
  rootMargin: "0px 0px -80px 0px",
  threshold: 0.1,
  once: true,
};

/**
 * Wraps content and reveals it with an animation when it enters the viewport.
 * Use for section headings, cards, and content blocks where scroll-triggered reveal is appropriate.
 */
const RevealOnScroll = ({
  children,
  className,
  variant = "fade-up",
  delay = 0,
  as: Component = "div",
  options = {},
  ...props
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { rootMargin, threshold, once } = { ...defaultOptions, ...options };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, threshold, once]);

  return (
    <Component
      ref={ref}
      className={cn(
        "reveal-on-scroll",
        variant && `reveal-on-scroll--${variant}`,
        isVisible && "reveal-on-scroll--visible",
        className
      )}
      style={{
        transitionDelay: isVisible ? `${delay}ms` : undefined,
      }}
      {...props}
    >
      {children}
    </Component>
  );
};

export default RevealOnScroll;
